import CreateObjectDragger from './CreateObjectDragger';
import HybridizationAtom from '../model/HybridizationAtom';
import Side from '../model/side';
import Configuration from '../model/Configuration';
import Mode from '../model/Mode';
import PuzzleDataModel from '../model/PuzzleDataModel';
import PointerRotateBehavior from './PointerRotateBehavior';
import ConfigurationUtils from '../model/ConfigurationUtils';
import BackgroundController from './BackgroundController';
import Params from "../options/Params";

import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Axis, Space } from "@babylonjs/core/Maths/math.axis";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { FadeInOutBehavior } from "@babylonjs/core/Behaviors/Meshes/fadeInOutBehavior";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { LinesBuilder } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { EdgesRenderer } from "@babylonjs/core/Rendering/edgesRenderer";

export default class App {
  constructor(scene, props) {
    this.scene = scene;
    this.state = props;
    this.orbitalFactory = props.orbitalFactory;
    this.background = new BackgroundController(scene);
    this.draggers = [];

    // if (Params.mode === "author") {
    //   Params.configuration = "{}";
    // }
  }

  init() {
    this.skewer = this.createSkewer("skewer", this.scene);
    this.leftCenter = this.createCenterAt("leftCenter", Vector3.Left(), this.scene);
    this.rightCenter = this.createCenterAt("rightCenter", Vector3.Right(), this.scene);

    if (this.orbitalFactory) {
      this.piOrbitalY = this.orbitalFactory.createPi();
      this.piOrbitalY.isVisible = false;
      this.piOrbitalY.isPickable = false;

      this.piOrbitalZ = this.orbitalFactory.createPi();
      this.piOrbitalZ.isVisible = false;
      this.piOrbitalZ.isPickable = false;
      this.piOrbitalZ.rotate(Axis.X, Math.PI / 2);

      this.sigmaOrbital = this.orbitalFactory.createSigma();
      this.sigmaOrbital.isVisible = false;
    }

    if (Params.mode == "author" && !Params.configuration) {
      Params.configuration = "empty,0,1|empty,0,1";
    }

    this.background.init();
    this.reset(Params.configurationTemp);

    const sDraggerProps = {
      draggingMeshFn: this.orbitalFactory.createS.bind(this.orbitalFactory),
      scene: this.scene,
      onDragEnd: this.onDragEndS.bind(this)
    };
    this.draggers["s"] = new CreateObjectDragger(sDraggerProps);

    const pDraggerProps = {
      draggingMeshFn: this.orbitalFactory.createP.bind(this.orbitalFactory),
      scene: this.scene,
      onDragEnd: this.onDragEndP.bind(this)
    };
    this.draggers["p"] = new CreateObjectDragger(pDraggerProps);

    const dDraggerProps = {
      draggingMeshFn: this.orbitalFactory.createD.bind(this.orbitalFactory),
      scene: this.scene,
      onDragEnd: this.onDragEndD.bind(this)
    };
    this.draggers["d"] = new CreateObjectDragger(dDraggerProps);
  }

  render(dt) {
    this.scene.render();
    this.background.update(dt);
  }

  onHybridize() {
    this.hybridizeLeft();
    this.hybridizeRight();
    this.updateDescription(this.leftCenter, this.rightCenter, this.piCount);
  }

  onSubmit() {
    if (this.wasGoalCompleted()) {
      // console.log("completed");
      this.notifyUser("completed");
    } else {
      // console.log(`expected ${JSON.stringify(this.puzzle.goal)} but received ${JSON.stringify(finalState)}`);
      this.notifyUser("you are close");
    }
  }

  wasGoalCompleted() {
    const finalState = this.getFinalConfiguration();
    return this.puzzle.hasCompletedGoal(finalState);
  }

  getFinalConfiguration() {
    const currentConfigurations = [
      this.leftCenter.hybridizationAtom.configuration,
      this.rightCenter.hybridizationAtom.configuration
    ];
    const currentState = PuzzleDataModel.createGoalFromConfigurations(currentConfigurations);
    const finalState = this.adjustForPi(currentState);
    return finalState;
  }

  createOrbital(type) {
    this.draggers[type].onPointerOut();

    this.onOrbitalDragStart();
  }

  releaseOrbital(type) {
    this.draggers[type].onPointerUp();

    this.onOrbitalDragEnd();
  }

  moveOrbital(type, x, y) {
    this.draggers[type].onPointerMove(x, y);
  }

  adjustForPi(goal) {
    const numPi = this.piCount;
    if (numPi) {
      goal.pi = numPi;
      goal.p -= 2 * numPi;
      if (!goal.p) goal.p = undefined;
    }
    return goal;
  }

  get piCount() {
    return (this.piOrbitalY.isVisible ? 1 : 0) + (this.piOrbitalZ.isVisible ? 1 : 0);
  }

  onOrbitalDragStart() {
    this.leftCenter.hybridizationAtom.isPickable = false;
    this.rightCenter.hybridizationAtom.isPickable = false;
    this.leftCenter.outerShell.isPickable = true;
    this.rightCenter.outerShell.isPickable = true;
  }
  onOrbitalDragEnd() {
    this.leftCenter.hybridizationAtom.isPickable = true;
    this.rightCenter.hybridizationAtom.isPickable = true;
    this.leftCenter.outerShell.isPickable = false;
    this.rightCenter.outerShell.isPickable = false;
  }

  notifyUser(message) {
    // TODO: Do something better than an alert.
    alert(message);
  }

  reset(override) {
    this.leftCenter.dispose();
    this.rightCenter.dispose();
    this.leftCenter = this.createCenterAt("leftCenter", Vector3.Left(), this.scene);
    this.rightCenter = this.createCenterAt("rightCenter", Vector3.Right(), this.scene);

    this.turnOffPiBonds();
    this.turnOffSigmaBonds();

    const configuration = override || Params.configuration;

    if (configuration) {
      if (configuration.indexOf(",") > 0) {
        this.loadNewConfigurations(configuration);
      } else {
        this.loadConfigurations(configuration);
      }
    } else if (Params.goalconfiguration) {
      // For assessment, we start with empty unless specifically 
      // defined in the configuration.
      if (Array.isArray(Params.goalconfiguration)) {
        this.loadConfigurations(["empty", "empty"]);
      } else {
        this.loadConfigurations("empty");
      }
    }
    if (Params.mode) {
      if (Params.mode === Mode.Assess) {
        this.loadPuzzleParams(Params);
      }
    }

    this.updateDescription(this.leftCenter, this.rightCenter, this.piCount);
  }

  loadNewConfigurations(configuration) {
    const configData = configuration.split("|").map(x => x.split(","));
    const configOrbitals = configData.map(x => x[0]);

    if (configOrbitals.length == 1) {
      this.loadConfigurations(configOrbitals[0]);
      this.setNodeRotation(this.leftCenter, configData[0][1], configData[0][2]);
    } else {
      this.loadConfigurations(configOrbitals);
      this.setNodeRotation(this.leftCenter, configData[0][1], configData[0][2]);
      this.setNodeRotation(this.rightCenter, configData[1][1], configData[1][2]);
    }

    document.body.dispatchEvent(new CustomEvent("set-atom-count", { detail: { count: configuration.indexOf("|") > 0 ? 2 : 1 } }));
  }

  togglePointerEvents(toggled) {
    this.leftCenter.behaviors.find(x => x.name == "PointerRotate").enabled = toggled;
    this.rightCenter.behaviors.find(x => x.name == "PointerRotate").enabled = toggled;
  }

  loadConfigurationString(config) {
    Params.configuration = config;
    this.reset();
  }

  loadConfigurationStringTemp(config) {
    this.reset(config);
  }

  doesConfigMatch(goalConfigurations) {
    const currentConfig = this.getGoalConfiguration();

    for (let goal of goalConfigurations) {
      if (currentConfig == goal) {
        return true;
      }
    }

    return false;
  }

  setNodeRotation(node, xString, wString) {
    const rotationBehavior = node.behaviors.find(x => x.name == "PointerRotate");
    const x = parseFloat(xString);
    const w = parseFloat(wString);

    rotationBehavior.setRotation(x, w);
  }

  loadConfigurations(configuration) {
    if (configuration) {
      if (Array.isArray(configuration)) {
        if (configuration[0]) {
          this.configureCenter(this.leftCenter, configuration[0], Side.LEFT);
        }

        if (configuration[1]) {
          this.configureCenter(this.rightCenter, configuration[1], Side.RIGHT);
        }
      } else {
        this.configureCenter(this.leftCenter, configuration, Side.LEFT);
        this.rightCenter.setEnabled(false);
        this.leftCenter.position.copyFrom(new Vector3.Zero());
      }
    }
  }

  getConfigString() {
    if (this.rightCenter.isEnabled()) {
      return this.getConfigStringForNode(this.leftCenter) + "|" + this.getConfigStringForNode(this.rightCenter);
    }

    return this.getConfigStringForNode(this.leftCenter);
  }

  getGoalConfiguration() {
    const finalConfig = this.getFinalConfiguration();
    const keys = Object.getOwnPropertyNames(finalConfig).filter(x => finalConfig[x]);
    const config = keys.map(x => x + ":" + finalConfig[x]).join(",");

    return config;
  }

  getConfigStringForNode(node) {
    const rotationX = Math.round(node.rotationQuaternion.x * 1000) / 1000 || 0;
    const rotationW = Math.round(node.rotationQuaternion.w * 1000) / 1000 || 1;

    return node.hybridizationAtom.configuration + "," + rotationX + "," + rotationW;
  }

  setAtomCount(count) {
    if (count == 1) {
      Params.configuration = "{}";
      this.reset();
    } else if (count == 2) {
      Params.configuration = [];
      this.reset();
    }
  }

  loadPuzzleParams(settings) {
    this.puzzle = new PuzzleDataModel(settings);

    const elem = document.getElementById('react-app');
    if (elem) {
      const event = new CustomEvent('set-goal', { detail: { goal: this.puzzle.goal } });
      elem.dispatchEvent(event);
    }
  }

  createSkewer(name, scene) {
    const skewerOptions = {
      points: [
        new Vector3(-5, 0, 0),
        new Vector3(5, 0, 0)
      ],
      dashNb: 12
    }
    const skewer = LinesBuilder.CreateDashedLines(name, skewerOptions, scene);

    skewer.enableEdgesRendering();
    skewer.alpha = 0;
    skewer.edgesWidth = 8.0;
    skewer.edgesColor = Params.theme == "paper" ? new Color4(0.3, 0.3, 0.3, 1) : new Color4(1, 1, 1, 0.8);;
    skewer.isPickable = false;

    return skewer;
  }

  createCenterAt(name, position, scene) {

    if (!this.centerMat) {
      const sphereMat = new StandardMaterial("center", scene);
      sphereMat.diffuseColor = new Color3(0.25, 0.25, 0.25);
      sphereMat.specularColor = new Color3(0.2, 0.2, 0.2);
      this.centerMat = sphereMat;
    }

    const center = MeshBuilder.CreateSphere(name, { diameter: .25 }, scene);
    center.position.copyFrom(position);
    center.material = this.centerMat;
    center.hybridizationAtom = new HybridizationAtom();
    center.hybridizationAtom.root = center;

    const outerCenter = MeshBuilder.CreateSphere(name + " outer", { diameter: 2 }, scene);
    outerCenter.material = this.centerMat;
    outerCenter.isPickable = false;
    outerCenter.visibility = 0;
    outerCenter.parent = center;
    outerCenter.hybridizationAtom = center.hybridizationAtom;
    center.outerShell = outerCenter;

    center.rotationQuaternion = new Quaternion();

    this.addRotaterToCenter(center);

    return center;
  }

  addRotaterToCenter(center) {
    const rotater = new PointerRotateBehavior();
    rotater.onRotateObservable.add((data, state) => {
      this.checkForPiBond();
    });


    center.addBehavior(rotater, true);
  }

  onDragEndS(mesh, pick) {
    if (this.isValidDrag(mesh, pick) && pick.pickedMesh.hybridizationAtom.canPlaceS()) {
      this.finalizeDrag(mesh, pick);
      pick.pickedMesh.hybridizationAtom.sOrbital = mesh;
      this.updateDescription(this.leftCenter, this.rightCenter, this.piCount);
    } else {
      mesh.dispose();
    }
  }

  onDragEndP(mesh, pick) {
    if (this.isValidDrag(mesh, pick) &&
      pick.pickedMesh.hybridizationAtom.canPlaceP()) {
      this.finalizeDrag(mesh, pick);
      pick.pickedMesh.hybridizationAtom.addPOrbital(mesh);

      this.checkForPiBond();
      this.checkForSigmaBond();

      this.updateDescription(this.leftCenter, this.rightCenter, this.piCount);
    } else {
      mesh.dispose();
    }
  }

  onDragEndS(mesh, pick) {
    if (this.isValidDrag(mesh, pick) && pick.pickedMesh.hybridizationAtom.canPlaceS()) {
      this.finalizeDrag(mesh, pick);
      pick.pickedMesh.hybridizationAtom.sOrbital = mesh;
      this.updateDescription(this.leftCenter, this.rightCenter, this.piCount);
    } else {
      mesh.dispose();
    }
  }

  onDragEndD(mesh, pick) {
    if (this.isValidDrag(mesh, pick) &&
      pick.pickedMesh.hybridizationAtom.canPlaceD()) {
      this.finalizeDrag(mesh, pick);
      pick.pickedMesh.hybridizationAtom.addDOrbital(mesh);

      this.updateDescription(this.leftCenter, this.rightCenter, this.piCount);
    } else {
      mesh.dispose();
    }
  }

  isValidDrag(mesh, pick) {
    return pick && pick.pickedMesh && pick.pickedMesh.hybridizationAtom;
  }

  finalizeDrag(mesh, pick) {
    //flash effect
    this.fadeBehavior = new FadeInOutBehavior();
    this.fadeBehavior.fadeInTime = 500;
    mesh.addBehavior(this.fadeBehavior);
    this.fadeBehavior.fadeIn(true);
    this.finalizeMesh(mesh, pick.pickedMesh);
  }

  finalizeMesh(mesh, parent) {
    mesh.parent = parent;
    mesh.position.copyFrom(Vector3.Zero());
    mesh.isPickable = true;
    mesh.isVisible = true;
  }

  animate(meshes) {
    for (var i = 0; i < meshes.length; i = i + 2) {
      this.fadeBehavior = new FadeInOutBehavior();
      this.fadeBehavior.delay = i * 250;
      meshes[i].addBehavior(this.fadeBehavior);
      this.fadeBehavior.fadeIn(true);

      this.fadeBehavior = new FadeInOutBehavior();
      this.fadeBehavior.delay = i * 250;
      meshes[i + 1].addBehavior(this.fadeBehavior);
      this.fadeBehavior.fadeIn(true);
    }
  }

  hybridizeLeft() {
    const configuration = this.leftCenter.hybridizationAtom.hybridizableState;
    if (configuration) {
      this.configureCenter(this.leftCenter, configuration, Side.LEFT);
      if (this.leftCenter.hybridizationAtom.spOrbital) {
        this.animate(this.leftCenter.hybridizationAtom.spOrbital.meshes);
      }
    }
  }

  hybridizeRight() {
    const configuration = this.rightCenter.hybridizationAtom.hybridizableState;
    if (configuration) {
      this.configureCenter(this.rightCenter, configuration, Side.RIGHT);
      if (this.rightCenter.hybridizationAtom.spOrbital) {
        this.animate(this.rightCenter.hybridizationAtom.spOrbital.meshes);
      }
    }
  }

  checkForSigmaBond() {
    //console.log("In check for sigma bond");
    if (this.leftCenter.hybridizationAtom.spOrbital && this.rightCenter.hybridizationAtom.spOrbital) {

      this.sigmaOrbital.isVisible = true;
      this.leftCenter.hybridizationAtom.spOrbital.meshes[0].isVisible = false;
      this.leftCenter.hybridizationAtom.spOrbital.meshes[1].isVisible = false;

      this.rightCenter.hybridizationAtom.spOrbital.meshes[0].isVisible = false;
      this.rightCenter.hybridizationAtom.spOrbital.meshes[1].isVisible = false;
    }
    else {
      this.turnOffSigmaBonds();
    }
  }


  turnOffSigmaBonds() {
    if (this.sigmaOrbital) {

      this.sigmaOrbital.isVisible = false;

      if (this.leftCenter.hybridizationAtom.spOrbital) {
        this.leftCenter.hybridizationAtom.spOrbital.meshes[0].isVisible = true;
        this.leftCenter.hybridizationAtom.spOrbital.meshes[1].isVisible = true;
      }

      if (this.rightCenter.hybridizationAtom.spOrbital) {
        this.rightCenter.hybridizationAtom.spOrbital.meshes[0].isVisible = true;
        this.rightCenter.hybridizationAtom.spOrbital.meshes[1].isVisible = true;
      }
    }
  }


  checkForPiBond() {
    if (this.leftCenter.rotationQuaternion && this.rightCenter.rotationQuaternion &&
      this.leftCenter.hybridizationAtom.spOrbital != null && this.rightCenter.hybridizationAtom.spOrbital != null &&
      this.leftCenter.hybridizationAtom.pOrbitals.length > 0 && this.rightCenter.hybridizationAtom.pOrbitals.length > 0) {

      let leftAngle = this.leftCenter.rotationQuaternion.toEulerAngles().x;
      let rightAngle = this.rightCenter.rotationQuaternion.toEulerAngles().x;

      let matched_y = this.areAnglesAligned(leftAngle, rightAngle);
      let matched_z = false;


      let morePAtom = this.leftCenter;
      let fewerPAtom = this.rightCenter;
      let morePCount = morePAtom.hybridizationAtom.totalPCount;
      let fewerPCount = fewerPAtom.hybridizationAtom.totalPCount;

      if (fewerPCount > morePCount) {
        morePAtom = this.rightCenter;
        fewerPAtom = this.leftCenter;

        morePCount = morePAtom.hybridizationAtom.totalPCount;
        fewerPCount = fewerPAtom.hybridizationAtom.totalPCount;
      }

      let rightAngleZ = 0;

      if (morePCount >= 3) {
        rightAngleZ = rightAngle + Math.PI / 2;
        matched_z = this.areAnglesAligned(leftAngle, rightAngleZ);
      }

      if (matched_y || matched_z) {

        let piAngle = 0;

        if (matched_y) {
          piAngle = (leftAngle + rightAngle) / 2;
        } else { // matched_z
          piAngle = (leftAngle + rightAngleZ) / 2;
        }

        let leftPCount = this.leftCenter.hybridizationAtom.pOrbitals.length;
        let rightPCount = this.rightCenter.hybridizationAtom.pOrbitals.length;
        let leftHybridizedPCount = this.leftCenter.hybridizationAtom.hybridizedPCount;
        let rightHybridizedPCount = this.rightCenter.hybridizationAtom.hybridizedPCount;


        // Check for pi bond aligned with leftAtom y.
        if (matched_y && leftHybridizedPCount < 2 && rightHybridizedPCount < 2 ||
          matched_z && leftHybridizedPCount < 2 && rightPCount + rightHybridizedPCount === 3) {

          this.piOrbitalY.rotationQuaternion = this.leftCenter.rotationQuaternion.clone();// new Quaternion(piAngle, 0, 0);
          this.piOrbitalY.isVisible = true;

          let leftPIndex = 1 - leftHybridizedPCount;
          if (leftPIndex < this.leftCenter.hybridizationAtom.pOrbitals.length) {
            this.leftCenter.hybridizationAtom.pOrbitals[leftPIndex].isVisible = false;
          }

          let rightPIndex = 0;
          if (matched_y) {
            rightPIndex = 1 - rightHybridizedPCount;
          } else {
            rightPIndex = 2 - rightHybridizedPCount;
          }

          if (rightPIndex < this.rightCenter.hybridizationAtom.pOrbitals.length) {
            this.rightCenter.hybridizationAtom.pOrbitals[rightPIndex].isVisible = false;
          }

        }


        // Check for pi bond aligned with leftAtom z.
        if (matched_y && leftPCount + leftHybridizedPCount === 3 && rightPCount + rightHybridizedPCount === 3 ||
          matched_z && leftPCount + leftHybridizedPCount === 3 && rightHybridizedPCount < 2) {

          this.piOrbitalZ.rotationQuaternion = this.leftCenter.rotationQuaternion.clone();// new Quaternion(piAngle + 90, 0, 0);
          this.piOrbitalZ.rotate(Axis.X, Math.PI / 2, Space.WORLD);
          this.piOrbitalZ.isVisible = true;

          let leftPIndex = 2 - leftHybridizedPCount;
          if (leftPIndex < this.leftCenter.hybridizationAtom.pOrbitals.length) {
            this.leftCenter.hybridizationAtom.pOrbitals[leftPIndex].isVisible = false;
          }


          let rightPIndex = 0;
          if (matched_y) {
            rightPIndex = 2 - rightHybridizedPCount;
          } else {
            rightPIndex = 1 - rightHybridizedPCount;
          }

          if (rightPIndex < this.rightCenter.hybridizationAtom.pOrbitals.length) {
            this.rightCenter.hybridizationAtom.pOrbitals[rightPIndex].isVisible = false;
          }

        }
      }
      else {
        this.turnOffPiBonds();
      }

    }
    else {
      this.turnOffPiBonds();
    }
  }


  areAnglesAligned(angle1, angle2) {
    let alignmentAngle = this.getAlignmentAngle(angle2 - angle1);

    return Math.abs(alignmentAngle) < Math.PI / 18;
  }


  getAlignmentAngle(angle) {

    // Make sure the angle is positive.
    if (angle < 0) {
      angle = angle % (Math.PI * 2) + (Math.PI * 2);
    }

    // Change angle to be below 180.
    angle %= Math.PI;

    // Change angle to be between -90 and 90.
    if (angle > Math.PI / 2) {
      angle -= Math.PI;
    }

    return angle;
  }


  turnOffPiBonds() {
    this.piOrbitalY.isVisible = false;
    this.piOrbitalZ.isVisible = false;

    for (let i = 0; i < this.leftCenter.hybridizationAtom.pOrbitals.length; i++) {
      this.leftCenter.hybridizationAtom.pOrbitals[i].isVisible = true;
    }

    for (let i = 0; i < this.rightCenter.hybridizationAtom.pOrbitals.length; i++) {
      this.rightCenter.hybridizationAtom.pOrbitals[i].isVisible = true;
    }
  }


  configureCenter(center, configuration, side) {
    center.hybridizationAtom.reset();

    if (configuration === Configuration.sp ||
      configuration === Configuration.spP ||
      configuration === Configuration.spPP) {
      const sp = this.orbitalFactory.createSP(side);
      center.hybridizationAtom.addSPOrbital(sp);
      this.finalizeMesh(sp, center)
    } else if (configuration === Configuration.sp2 ||
      configuration === Configuration.sp2P) {
      const sp2 = this.orbitalFactory.createSP2(side);
      center.hybridizationAtom.addSPOrbital(sp2);
      this.finalizeMesh(sp2, center)
    } else if (configuration === Configuration.sp3) {
      const sp3 = this.orbitalFactory.createSP3(side);
      center.hybridizationAtom.addSPOrbital(sp3);
      this.finalizeMesh(sp3, center)
    } else if (configuration === Configuration.sp3d) {
      const sp3d = this.orbitalFactory.createSP3D(side);
      center.hybridizationAtom.addSPOrbital(sp3d);
      this.finalizeMesh(sp3d, center)
    } else if (configuration === Configuration.sp3d2) {
      const sp3d2 = this.orbitalFactory.createSP3D2(side);
      center.hybridizationAtom.addSPOrbital(sp3d2);
      this.finalizeMesh(sp3d2, center)
    }

    let i;
    const nums = ConfigurationUtils.countS(configuration);
    if (nums > 0) {
      const s = this.orbitalFactory.createS();
      center.hybridizationAtom.sOrbital = s;
      this.finalizeMesh(s, center);
    }

    const nump = ConfigurationUtils.countP(configuration);
    for (i = 0; i < nump; i++) {
      const P = this.orbitalFactory.createP();
      center.hybridizationAtom.addPOrbital(P);
      this.finalizeMesh(P, center);
    }

    const numd = ConfigurationUtils.countD(configuration);
    for (i = 0; i < numd; i++) {
      const D = this.orbitalFactory.createD();
      center.hybridizationAtom.addDOrbital(D);
      this.finalizeMesh(D, center);
    }

    this.checkForPiBond();
    this.checkForSigmaBond();
  }
  getSceneDescription(configL, configR, numPi) {

    let leftDescription;
    let rightDescription;
    let a = ['a pi bond', 'two pi bonds', 'three pi bonds', 'four pi bonds'];

    const pi = (numPi === 0) ? "" : " and " + a[numPi - 1];

    if (this.rightCenter && this.rightCenter._isEnabled === false) {
      return "you have " + ConfigurationUtils.getDescription(configL);
    } else {
      if (ConfigurationUtils.checkForSigma(configL, configR)) {
        if (numPi == 0) {
          leftDescription = (Configuration.empty === configL) ? "nothing" : ConfigurationUtils.getDescriptionWithSigma(configL);
          leftDescription += " in the middle you have a sigma bond" + pi;
          rightDescription = (Configuration.empty === configR) ? "nothing" : ConfigurationUtils.getDescriptionWithSigma(configR);
        }
        if (numPi == 1) {
          leftDescription = (Configuration.empty === configL) ? "nothing" : ConfigurationUtils.getDescriptionWithOnePi(configL);
          leftDescription += " in the middle you have a sigma bond" + pi;
          rightDescription = (Configuration.empty === configR) ? "nothing" : ConfigurationUtils.getDescriptionWithOnePi(configR);
        }
        if (numPi == 2) {
          leftDescription = (Configuration.empty === configL) ? "nothing" : ConfigurationUtils.getDescriptionWithTwoPi(configL);
          leftDescription += " in the middle you have a sigma bond" + pi;
          rightDescription = (Configuration.empty === configR) ? "nothing" : ConfigurationUtils.getDescriptionWithTwoPi(configR);
        }
      } else {
        leftDescription = (Configuration.empty === configL) ? "nothing" : ConfigurationUtils.getDescription(configL);
        rightDescription = (Configuration.empty === configR) ? "nothing" : ConfigurationUtils.getDescription(configR);
      }

    }

    return "on the left you have " + leftDescription
      + " and on the right you have " + rightDescription;

  }


  updateDescription(left, right, piNum) {
    let prompt;
    prompt = this.getSceneDescription(left.hybridizationAtom.configuration, right.hybridizationAtom.configuration, piNum);
    if (document.getElementById("renderCanvas")) {
      document.getElementById("renderCanvas").innerHTML = prompt;
    }
  }

}
